import React, { useState, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { useFilters } from '../../context/FilterContext';
import { useResponsive } from '../../hooks/useResponsive';
import { useDashboardData, getChartData } from '../../hooks/useDashboardData';
import { useMonthlyDayOfWeekTrendQuery } from '../../hooks/queries/useDashboardQuery';
import './DailySalesChart.css';

interface DayOfWeekData {
  day_of_week: number;
  day_name: string;
  net_sales: string;
  order_count: string;
  avg_daily_sales: string;
}


interface ModalPosition {
  y: number;
}

const extractNumber = (value: string): number => {
  if (typeof value === 'string') {
    return parseFloat(value.replace(/[^0-9.-]+/g, ''));
  }
  return value || 0;
};

const formatMonthForDisplay = (yearMonth: string) => {
  const [year, month] = yearMonth.split('-');
  return `${year}/${month}`;
};

const calculateYAxisRange = (values: number[]) => {
  const validValues = values.filter(v => v > 0 && isFinite(v));
  
  if (validValues.length === 0) {
    return { min: 0, max: 100 };
  }
  
  const minValue = Math.min(...validValues);
  const maxValue = Math.max(...validValues);
  const range = maxValue - minValue;
  
  return {
    min: Math.max(0, Math.floor(minValue - range * 0.05)),
    max: Math.ceil(maxValue + range * 0.05)
  };
};

const DailySalesChart: React.FC = () => {
  const { t } = useTranslation();
  const { data: dashboardData, loading, error, salesforceId } = useDashboardData();
  const { 
    isFiltersApplying,
    dayOfWeek,
    time,
    diningBehavior 
  } = useFilters();

  const [showMonthlyTrend, setShowMonthlyTrend] = useState<boolean>(false);
  const [modalPosition, setModalPosition] = useState<ModalPosition>({ y: 0 });
  
  
  const { 
    data: monthlyTrendData, 
    isLoading: trendLoading, 
    error: trendQueryError
  } = useMonthlyDayOfWeekTrendQuery(salesforceId || '', showMonthlyTrend && !!salesforceId);
  
  const trendError = trendQueryError ? t('dailyChart.monthlyTrendError') : null;
  
  const trendChartRef = useRef<any>(null);
  
  const [allLegendsSelected, setAllLegendsSelected] = useState<boolean>(true);
  
  const colorPalette = {
    primaryColor: '#F97F76',
    highlightColor: '#F52314', 
    textColor: '#333333' 
  };

  const dayColors = {
    0: '#F97F76', 
    1: '#474646', 
    2: '#4285F4',
    3: '#34A853', 
    4: '#FBBC05', 
    5: '#EA4335', 
    6: '#9C27B0' 
  };

  const { isLaptop, isiPhone } = useResponsive();
  const isPhoneScreen = window.innerWidth <= 480;

  const calculateModalPosition = (event: React.MouseEvent): ModalPosition => {
    const clickY = event.clientY;
    const modalHeight = 700;
    const padding = 20;
    const screenHeight = window.innerHeight;
    
    let finalY = clickY;
    
    if (clickY + modalHeight + padding > screenHeight) {
      finalY = screenHeight - modalHeight - padding;
    }
    
    if (finalY < padding) {
      finalY = padding;
    }
    
    return { y: finalY };
  };



  const handleToggleAllLegends = () => {
    if (trendChartRef.current) {
      const chartInstance = trendChartRef.current.getEchartsInstance();
      const dayNames = [
        t('dailyChart.dayNames.sunday'),
        t('dailyChart.dayNames.monday'),
        t('dailyChart.dayNames.tuesday'),
        t('dailyChart.dayNames.wednesday'),
        t('dailyChart.dayNames.thursday'),
        t('dailyChart.dayNames.friday'),
        t('dailyChart.dayNames.saturday')
      ];

      if (allLegendsSelected) {
        dayNames.forEach(dayName => {
          chartInstance.dispatchAction({
            type: 'legendUnSelect',
            name: dayName
          });
        });
        setAllLegendsSelected(false);
      } else {
        dayNames.forEach(dayName => {
          chartInstance.dispatchAction({
            type: 'legendSelect',
            name: dayName
          });
        });
        setAllLegendsSelected(true);
      }
    }
  };

  const getFilteredDayOfWeekData = (): DayOfWeekData[] => {

    
    if (!dashboardData) {
      console.warn('❌ DailySalesChart: No dashboard data available');
      return [];
    }

    const filters = {
      dayOfWeek: (!dayOfWeek.allDays && dayOfWeek.selectedDays.length > 0) ? dayOfWeek.selectedDays : undefined,
      hour: (!time.allHours && time.selectedHours.length > 0) ? time.selectedHours : undefined,
      behavior: (!diningBehavior.allBehaviors && diningBehavior.selectedBehaviors.length > 0) ? diningBehavior.selectedBehaviors : undefined,
      diningGroup: undefined,
      menuGroup: undefined
    };


    const chartData = getChartData(dashboardData, 'dayOfWeek', filters);
    
    if (!chartData || !Array.isArray(chartData)) {
      return []; 
    }

    return chartData;
  };

  const handleMonthlyTrendClick = (event: React.MouseEvent) => {
    const position = calculateModalPosition(event);
    setModalPosition(position);
    setShowMonthlyTrend(true);
  };

  const closeMonthlyTrend = () => {
    setShowMonthlyTrend(false);
    setAllLegendsSelected(true);
  };

  const getMonthlyTrendOption = () => {
    if (!monthlyTrendData || monthlyTrendData.length === 0) {
      return {};
    }

    const months = monthlyTrendData.map((item: any) => formatMonthForDisplay(item.year_month));
    
    const series: any[] = [];
    const legendData: string[] = [];
    const allValues: number[] = []; 

    const dayOrder = [0, 1, 2, 3, 4, 5, 6];
    const dayNames = [
      t('dailyChart.dayNames.sunday'),
      t('dailyChart.dayNames.monday'),
      t('dailyChart.dayNames.tuesday'),
      t('dailyChart.dayNames.wednesday'),
      t('dailyChart.dayNames.thursday'),
      t('dailyChart.dayNames.friday'),
      t('dailyChart.dayNames.saturday')
    ];

    dayOrder.forEach((dayOfWeek, index) => {
      const dayName = dayNames[index];
      const dayData = monthlyTrendData.map((monthData: any) => {
        const dayInfo = monthData.days[dayOfWeek];
        const value = dayInfo ? extractNumber(dayInfo.avg_daily_sales) : 0;
        if (value > 0) allValues.push(value);
        return value;
      });

      series.push({
        name: dayName,
        type: 'line',
        data: dayData,
        lineStyle: {
          width: isLaptop ? 2.5 : 3,
          color: dayColors[dayOfWeek as keyof typeof dayColors]
        },
        itemStyle: {
          color: dayColors[dayOfWeek as keyof typeof dayColors]
        },
        symbol: 'circle',
        symbolSize: isLaptop ? 5 : 6,
        smooth: false
      });

      legendData.push(dayName);
    });

    const yAxisRange = calculateYAxisRange(allValues);

    return {
      grid: {
        left: '4%',
        right: '4%',
        top: (window.innerWidth <= 480) ? '28%' : (isLaptop ? '16%' : '18%'),
        bottom: (window.innerWidth <= 480) ? '0%' : '2%',
        containLabel: true
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#333',
          fontSize: isLaptop ? 10 : 11
        },
        confine: true, 
        formatter: function(params: any) {
          const monthStr = params[0].axisValue;
          let html = `<div style="padding: 3px;">
            <div style="font-weight: bold; margin-bottom: ${isLaptop ? '5px' : '6px'}; font-size: ${isLaptop ? '11px' : '12px'};">${monthStr}</div>`;
          
          params.forEach((param: any) => {
            html += `<div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
              <span style="margin-right: 10px; display: inline-flex; align-items: center;">
                <span style="display: inline-block; width: 8px; height: 8px; background-color: ${param.color}; margin-right: 4px; border-radius: 50%;"></span>
                <strong>${param.seriesName}:</strong>
              </span>
              <span style="font-weight: bold; font-size: ${isLaptop ? '10px' : '11px'};">$${param.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>`;
          });
          
          html += '</div>';
          return html;
        }
      },
      legend: {
        data: legendData,
        top: isLaptop ? '5%' : '6%',
        textStyle: {
          fontSize: (window.innerWidth <= 480) ? 10 : (isLaptop ? 11 : 12),
          fontWeight: 'bold'
        },
        itemGap: isLaptop ? 12 : 15,
        type: (window.innerWidth <= 480) ? 'plain' : 'scroll',
        orient: 'horizontal',
        selector: false,
        itemStyle: {
          borderWidth: 0
        },
        lineStyle: {
          width: isLaptop ? 2.5 : 3
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0,0,0,0.3)'
          }
        }
      },
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: '1%',
          style: {
            text: t('dailyChart.chartHint'),
            fontSize: isLaptop ? 9 : 10,
            fontWeight: 'normal',
            fill: '#666',
            fontStyle: 'italic'
          }
        }
      ],
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: {
          fontSize: isLaptop ? 9 : 10,
          fontWeight: 'bold',
          rotate: months.length > 8 ? 45 : 0,
          margin: isLaptop ? 5 : 6
        },
        axisTick: {
          alignWithLabel: true
        }
      },
      yAxis: {
        type: 'value',
        name: t('dailyChart.yAxisDailyUSD'),
        min: yAxisRange.min,
        scale: true, 
        nameTextStyle: {
          fontSize: isLaptop ? 10 : 11,
          fontWeight: 'bold'
        },
        axisLabel: {
          fontSize: isLaptop ? 9 : 10,
          
          formatter: '${value}'
        },
        splitLine: {
          lineStyle: {
            opacity: 0.3
          }
        }
      },
      series: series
    };
  };

  const getOption = () => {
    const dayOfWeekData = getFilteredDayOfWeekData();
    
    if (dayOfWeekData.length === 0) {
      return {};
    }
    
    const sortedData = [...dayOfWeekData].sort((a, b) => {
      const dayA = (a.day_of_week + 6) % 7;
      const dayB = (b.day_of_week + 6) % 7;
      return dayA - dayB;
    });
    
    const dayLabels = sortedData.map(day => {
      if (window.innerWidth <= 480) {
        
        const shortNames: { [key: string]: string } = {
          'Monday': 'Mon',
          'Tuesday': 'Tue', 
          'Wednesday': 'Wed',
          'Thursday': 'Thu',
          'Friday': 'Fri',
          'Saturday': 'Sat',
          'Sunday': 'Sun'
        };
        return shortNames[day.day_name] || day.day_name;
      }
      return day.day_name;
    });
    
    const salesData = sortedData.map(day => extractNumber(day.avg_daily_sales));
    
    const maxSales = Math.max(...salesData);
    const minSales = Math.min(...salesData);
    const maxIndex = salesData.indexOf(maxSales);
    const minIndex = salesData.indexOf(minSales);
    
    const formattedSalesData = salesData.map((value, index) => {
      if (index === maxIndex) {
        return {
          value,
          itemStyle: {
            color: colorPalette.highlightColor,
            borderRadius: [6, 6, 0, 0]
          },
          label: {
            show: true,
            position: 'top',
            formatter: `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
            fontSize: isLaptop ? 12 : 14,
            fontWeight: 'bold',
            color: colorPalette.highlightColor
          }
        };
      } else if (index === minIndex) {
        return {
          value,
          itemStyle: {
            color: '#8A9297', 
            borderRadius: [6, 6, 0, 0]
          },
          label: {
            show: true,
            position: 'top',
            formatter: `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
            fontSize: isLaptop ? 12 : 14,
            fontWeight: 'bold',
            color: '#8A9297'
          }
        };
      } else {
        return {
          value,
          itemStyle: {
            color: colorPalette.primaryColor,
            borderRadius: [6, 6, 0, 0]
          }
        };
      }
    });
    
    return {
      tooltip: {
        trigger: 'axis',
        confine: true, 
        formatter: function(params: any) {
          const dayIndex = params[0].dataIndex;
          const dayData = sortedData[dayIndex];
          const totalSales = extractNumber(dayData.net_sales);
          const orderCount = dayData.order_count;
          const avgSales = extractNumber(dayData.avg_daily_sales);
          
          return `<div style="padding: 3px; border-left: 4px solid ${colorPalette.primaryColor};">
            <div style="font-weight: bold; margin-bottom: ${isLaptop ? '6px' : '8px'}; font-size: ${isLaptop ? '13px' : '14px'};">${dayData.day_name}</div>
            <div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? '4px' : '5px'};">
              <span style="margin-right: 15px;">${t('dailyChart.avgDailySales')}:</span>
              <span style="font-weight: bold;">$${avgSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? '4px' : '5px'};">
              <span style="margin-right: 15px;">${t('dailyChart.totalSales')}:</span>
              <span style="font-weight: bold;">$${totalSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span style="margin-right: 15px;">${t('dailyChart.orders')}:</span>
              <span style="font-weight: bold;">${orderCount}</span>
            </div>
          </div>`;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: isLaptop ? '45px' : '50px', 
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: dayLabels,
        axisLabel: {
          fontWeight: 'bold',
          fontSize: isLaptop ? 10 : 12,
          color: colorPalette.textColor
        },
        axisTick: {
          alignWithLabel: true
        }
      },
      yAxis: {
        type: 'value',
        name: t('dailyChart.yAxisDailyUSD'),
        nameTextStyle: {
          fontSize: isLaptop ? 11 : 12,
          color: colorPalette.textColor
        },
        axisLabel: {
          
          formatter: '${value}',
          fontWeight: 'bold',
          fontSize: isLaptop ? 11 : 12,
          color: colorPalette.textColor
        }
      },
      series: [
        {
          name: t('dailyChart.avgDailySales'),
          type: 'bar',
          barWidth: isLaptop ? '45%' : '50%',
          data: formattedSalesData
        }
      ]
    };
  };

  if (loading || isFiltersApplying) {
    return (
      <div className="daily-sales-chart">
        <div className="daily-title-band">
          <h2 style={{
            fontSize: isLaptop ? '16px' : '18px',
            padding: isLaptop ? '8px 20px' : '10px 25px'
          }}>
            {t('dailyChart.title')}
          </h2>
        </div>
        <div className="daily-chart-container">
          <div className="daily-loading">
            <div className="daily-loading-spinner"></div>
            <div className="daily-loading-text">
              {isFiltersApplying ? t('dailyChart.filterApplying') : t('dailyChart.loading')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="daily-sales-chart">
        <div className="daily-title-band">
          <h2 style={{
            fontSize: isLaptop ? '16px' : '18px',
            padding: isLaptop ? '8px 20px' : '10px 25px'
          }}>
            {t('dailyChart.title')}
          </h2>
        </div>
        <div className="daily-chart-container">
          <div className="daily-error">
            <div className="daily-error-icon">⚠️</div>
            <div className="daily-error-text">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="daily-sales-chart">
        {isFiltersApplying && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <span>{t('dailyChart.updating')}</span>
          </div>
        )}
        
        <div className="daily-title-band">
          <h2 style={{
            fontSize: isLaptop ? '16px' : '18px',
            padding: isLaptop ? '8px 20px' : '10px 25px'
          }}>
            {t('dailyChart.title')}
          </h2>
        </div>
        <div className="daily-chart-container">
          <div className="daily-navigation">
            <button 
              className="daily-monthly-trend-button" 
              onClick={handleMonthlyTrendClick}
              disabled={isFiltersApplying}
              style={{
                fontSize: isLaptop ? '11px' : '12px',
                padding: isLaptop ? '5px 10px' : '6px 12px'
              }}
            >
              {t('dailyChart.viewMonthlyTrend')}
            </button>
          </div>
          <ReactECharts 
            option={getOption()} 
            style={{ height: isiPhone ? '300px' : isLaptop ? '380px' : '460px', width: '100%' }}
            className="daily-echarts"
            opts={{ renderer: 'canvas' }}
          />
        </div>
      </div>

      {showMonthlyTrend && (
        <div className="daily-trend-overlay" onClick={closeMonthlyTrend}>
          <div 
            className="daily-trend-modal" 
            onClick={(e) => e.stopPropagation()}
            style={isPhoneScreen ? {
              position: 'fixed',
              left: '2.5%',
              top: `${modalPosition.y}px`,
              width: '95%',
              height: '500px',
              margin: '0',
              transform: 'none'
            } : {
              position: 'fixed',
              left: '50%',
              top: `${modalPosition.y}px`,
              transform: 'translateX(-50%)'
            }}
          >
            <div className="daily-trend-modal-header">
              <h3 className="daily-trend-modal-title">{t('dailyChart.modalTitle')}</h3>
              <div className="daily-trend-modal-subtitle">{t('dailyChart.modalSubtitle')}</div>
              <button className="daily-trend-modal-close" onClick={closeMonthlyTrend}>
                ✕
              </button>
            </div>
            <div className="daily-trend-modal-content">
              {trendLoading ? (
                <div className="daily-trend-loading">
                  <div className="daily-trend-loader"></div>
                  <div>{t('dailyChart.loadingMonthlyTrend')}</div>
                </div>
              ) : trendError ? (
                <div className="daily-trend-error">
                  <div className="daily-trend-error-icon">⚠️</div>
                  <div>{trendError}</div>
                </div>
              ) : monthlyTrendData && monthlyTrendData.length > 0 ? (
                <div className="daily-trend-chart-wrapper">
                  <div className="daily-legend-control-top">
                    <span className="daily-legend-hint">{t('dailyChart.legendHint')}</span>
                    <button 
                      className="daily-toggle-all-button"
                      onClick={handleToggleAllLegends}
                      type="button"
                    >
                      {allLegendsSelected ? t('dailyChart.deselectAll') : t('dailyChart.selectAll')}
                    </button>
                  </div>
                  <ReactECharts 
                    ref={trendChartRef}
                    option={getMonthlyTrendOption()} 
                    style={{height: '100%', width: '100%'}}
                    className="daily-trend-chart"
                    opts={{renderer: 'canvas'}}
                  />
                </div>
              ) : (
                <div className="daily-trend-empty">
                  <div>{t('dailyChart.noTrendData')}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DailySalesChart;